/**
 * The curriculum graph: Subject → Topic → Skill. Mastery is tracked per skill.
 * This is data. Moving it into the database (or an authoring tool) later must not change the shape.
 *
 * Curriculum mappings are PROVISIONAL: grade + strand only, and must be verified against
 * the official CAPS documents before the product claims alignment.
 */

import { FOUNDATION_TOPICS, FOUNDATION_SKILLS } from './foundation.ts';

export const SUBJECT_IDS = ['mathematics', 'natural-sciences', 'physical-sciences', 'life-sciences', 'home-language', 'english-fal', 'life-skills'] as const;
export type SubjectId = (typeof SUBJECT_IDS)[number];
export type CurriculumId = 'za-caps' | 'intl';

export interface Curriculum {
  id: CurriculumId;
  name: string;
  description: string;
  grades: number[];
}

export const CURRICULA: Curriculum[] = [
  {
    id: 'za-caps',
    name: 'South Africa — CAPS',
    description: 'Mathematics, Physical Sciences and Life Sciences as taught in South African schools.',
    grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  },
  {
    id: 'intl',
    name: 'International',
    description: 'Organised by grade and age rather than a national syllabus.',
    grades: [4, 5, 6, 7, 8, 9, 10, 11, 12],
  },
];

export interface Subject {
  id: SubjectId;
  name: string;
  /** Shown when choosing subjects: why this subject opens doors. */
  pitch: string;
}

export interface Topic {
  id: string;
  subjectId: SubjectId;
  name: string;
  /** "Physics" / "Chemistry" grouping inside Physical Sciences, for progress views. */
  strand?: string;
}

export interface Skill {
  id: string;
  topicId: string;
  name: string;
  /** One line: what you can do once you have this skill. */
  outcome: string;
  /** The skill's signature expression, in TeX. Shown large on Home and in Learn. */
  signature: string;
  /** Typical grade at which this is taught (CAPS-ish). */
  grade: number;
  prerequisites: string[];
  templateId: string;
  /** Senior skills have a lesson. Foundation Phase skills use "watch first" worked examples instead (lessonId ''). */
  lessonId: string;
  /** Grades 1–3 are 'foundation': played in the Junior experience with touch inputs and a voice. */
  phase?: 'foundation';
  mappings: Partial<Record<CurriculumId, { grade: number; strand: string }>>;
}

export const SUBJECTS: Subject[] = [
  {
    id: 'mathematics',
    name: 'Mathematics',
    pitch: 'The language of engineering, medicine, finance and every science. Pure maths keeps every door open.',
  },
  {
    id: 'natural-sciences',
    name: 'Natural Sciences & Technology',
    pitch: 'Living things, materials, energy, Earth and space, and how people design and build. Science for Grades 4 to 6.',
  },
  {
    id: 'physical-sciences',
    name: 'Physical Sciences',
    pitch: 'Physics and chemistry: how matter, energy and forces behave, and how to predict them.',
  },
  {
    id: 'life-sciences',
    name: 'Life Sciences',
    pitch: 'How living things are built, inherit traits and stay alive. It is the road to medicine.',
  },
  {
    id: 'home-language',
    name: 'Home Language',
    pitch: 'Sounds, reading, spelling and stories in the language a child speaks at home. Grades 1 to 3.',
  },
  {
    id: 'english-fal',
    name: 'English First Additional Language',
    pitch: 'Listening, words and reading in English for children whose home language is not English. Grades 1 to 3.',
  },
  {
    id: 'life-skills',
    name: 'Life Skills',
    pitch: 'Beginning Knowledge and Personal & Social Well-being: my body, safety, plants, animals, weather and feelings. Grades 1 to 3.',
  },
];

export const TOPICS: Topic[] = [
  { id: 'number', subjectId: 'mathematics', name: 'Number' },
  { id: 'fractions', subjectId: 'mathematics', name: 'Fractions and decimals' },
  { id: 'measurement', subjectId: 'mathematics', name: 'Measurement' },
  { id: 'algebra', subjectId: 'mathematics', name: 'Algebraic expressions and exponents' },
  { id: 'equations', subjectId: 'mathematics', name: 'Equations' },
  { id: 'functions', subjectId: 'mathematics', name: 'Functions and graphs' },
  { id: 'patterns', subjectId: 'mathematics', name: 'Number patterns and sequences' },
  { id: 'trigonometry', subjectId: 'mathematics', name: 'Trigonometry' },
  { id: 'finance', subjectId: 'mathematics', name: 'Financial mathematics' },
  { id: 'calculus', subjectId: 'mathematics', name: 'Calculus' },
  { id: 'nst-life', subjectId: 'natural-sciences', name: 'Life and living', strand: 'Life' },
  { id: 'nst-matter', subjectId: 'natural-sciences', name: 'Matter and materials', strand: 'Matter' },
  { id: 'nst-energy', subjectId: 'natural-sciences', name: 'Energy and change', strand: 'Energy' },
  { id: 'nst-earth', subjectId: 'natural-sciences', name: 'Planet Earth and beyond', strand: 'Earth and space' },
  { id: 'nst-tech', subjectId: 'natural-sciences', name: 'Technology: structures', strand: 'Technology' },
  { id: 'matter', subjectId: 'physical-sciences', name: 'Matter and materials', strand: 'Chemistry' },
  { id: 'energy', subjectId: 'physical-sciences', name: 'Energy and heat', strand: 'Physics' },
  { id: 'electricity', subjectId: 'physical-sciences', name: 'Electric circuits', strand: 'Physics' },
  { id: 'chemical-change', subjectId: 'physical-sciences', name: 'Chemical change', strand: 'Chemistry' },
  { id: 'mechanics', subjectId: 'physical-sciences', name: 'Mechanics', strand: 'Physics' },
  { id: 'biodiversity', subjectId: 'life-sciences', name: 'Biodiversity and classification', strand: 'Biology' },
  { id: 'ecology', subjectId: 'life-sciences', name: 'Ecosystems', strand: 'Biology' },
  { id: 'plants', subjectId: 'life-sciences', name: 'Plants and energy', strand: 'Biology' },
  { id: 'human-body', subjectId: 'life-sciences', name: 'The human body', strand: 'Biology' },
  { id: 'cells', subjectId: 'life-sciences', name: 'Cells', strand: 'Biology' },
  { id: 'genetics', subjectId: 'life-sciences', name: 'Genetics', strand: 'Biology' },
  ...FOUNDATION_TOPICS,
];

const caps = (grade: number, strand: string) => ({ 'za-caps': { grade, strand }, intl: { grade, strand } });

export const SKILLS: Skill[] = [
  // ---------- Mathematics ----------
  {
    id: 'place-value',
    topicId: 'number',
    name: 'Place value and rounding',
    outcome: 'Read, write, compare and round whole numbers up to 6 digits.',
    signature: '4\\,725 = 4\\,000 + 700 + 20 + 5',
    grade: 4,
    prerequisites: [],
    templateId: 'place-value',
    lessonId: 'place-value',
    mappings: caps(4, 'Numbers, operations and relationships'),
  },
  {
    id: 'multiplication',
    topicId: 'number',
    name: 'Multiplication and division',
    outcome: 'Multiply and divide whole numbers, including with remainders and in word problems.',
    signature: '7 \\times 8 = 7 \\times 5 + 7 \\times 3',
    grade: 4,
    prerequisites: ['place-value'],
    templateId: 'multiplication',
    lessonId: 'multiplication',
    mappings: caps(5, 'Numbers, operations and relationships'),
  },
  {
    id: 'integers',
    topicId: 'number',
    name: 'Operations with integers',
    outcome: 'Add, subtract, multiply and divide positive and negative numbers.',
    signature: '-7 - (-3) = -4',
    grade: 7,
    prerequisites: [],
    templateId: 'integers',
    lessonId: 'integers',
    mappings: caps(7, 'Numbers, operations and relationships'),
  },
  {
    id: 'order-of-operations',
    topicId: 'number',
    name: 'Order of operations',
    outcome: 'Evaluate expressions with brackets, powers and mixed operations correctly.',
    signature: '3 + 4 \\times 2^2 = 19',
    grade: 7,
    prerequisites: ['integers'],
    templateId: 'order-of-operations',
    lessonId: 'order-of-operations',
    mappings: caps(7, 'Numbers, operations and relationships'),
  },
  {
    id: 'fractions-basic',
    topicId: 'fractions',
    name: 'What fractions mean',
    outcome: 'Find fractions of shapes and amounts, and compare fractions.',
    signature: '\\tfrac{1}{3} \\text{ of } 12 = 4',
    grade: 4,
    prerequisites: [],
    templateId: 'fractions-basic',
    lessonId: 'fractions-basic',
    mappings: caps(4, 'Common fractions'),
  },
  {
    id: 'fraction-simplify',
    topicId: 'fractions',
    name: 'Equivalent fractions',
    outcome: 'Write fractions in simplest form and recognise equivalent fractions.',
    signature: '\\tfrac{18}{24} = \\tfrac{3}{4}',
    grade: 6,
    prerequisites: [],
    templateId: 'fraction-simplify',
    lessonId: 'fraction-simplify',
    mappings: caps(6, 'Common fractions'),
  },
  {
    id: 'fraction-add',
    topicId: 'fractions',
    name: 'Adding and subtracting fractions',
    outcome: 'Add and subtract fractions with different denominators.',
    signature: '\\tfrac{2}{3} + \\tfrac{1}{4} = \\tfrac{11}{12}',
    grade: 7,
    prerequisites: ['fraction-simplify'],
    templateId: 'fraction-add',
    lessonId: 'fraction-add',
    mappings: caps(7, 'Common fractions'),
  },
  {
    id: 'fraction-multiply',
    topicId: 'fractions',
    name: 'Multiplying and dividing fractions',
    outcome: 'Multiply and divide fractions, including dividing by a fraction.',
    signature: '\\tfrac{3}{4} \\div \\tfrac{1}{2} = \\tfrac{3}{2}',
    grade: 7,
    prerequisites: ['fraction-simplify'],
    templateId: 'fraction-multiply',
    lessonId: 'fraction-multiply',
    mappings: caps(7, 'Common fractions'),
  },
  {
    id: 'decimals',
    topicId: 'fractions',
    name: 'Decimals',
    outcome: 'Read, compare, add and subtract decimals, and convert between fractions and decimals.',
    signature: '0{,}5 = \\tfrac{1}{2} = 50\\%',
    grade: 5,
    prerequisites: ['fractions-basic'],
    templateId: 'decimals',
    lessonId: 'decimals',
    mappings: caps(6, 'Decimal fractions'),
  },
  {
    id: 'perimeter-area',
    topicId: 'measurement',
    name: 'Perimeter and area',
    outcome: 'Find the perimeter and area of rectangles and shapes made from them.',
    signature: 'A = l \\times b',
    grade: 5,
    prerequisites: ['multiplication'],
    templateId: 'perimeter-area',
    lessonId: 'perimeter-area',
    mappings: caps(5, 'Measurement: perimeter, area and volume'),
  },
  {
    id: 'like-terms',
    topicId: 'algebra',
    name: 'Simplifying expressions',
    outcome: 'Collect like terms to simplify algebraic expressions.',
    signature: '5x + 3y - 2x = 3x + 3y',
    grade: 8,
    prerequisites: ['integers'],
    templateId: 'like-terms',
    lessonId: 'like-terms',
    mappings: caps(8, 'Algebraic expressions'),
  },
  {
    id: 'expand-brackets',
    topicId: 'algebra',
    name: 'Expanding brackets',
    outcome: 'Multiply out brackets, including the product of two binomials.',
    signature: '(x + 2)(x + 3) = x^2 + 5x + 6',
    grade: 9,
    prerequisites: ['like-terms'],
    templateId: 'expand-brackets',
    lessonId: 'expand-brackets',
    mappings: caps(9, 'Algebraic expressions'),
  },
  {
    id: 'factorise-trinomial',
    topicId: 'algebra',
    name: 'Factorising trinomials',
    outcome: 'Factorise x² + bx + c and ax² + bx + c, taking out common factors first.',
    signature: 'x^2 + 5x + 6 = (x + 2)(x + 3)',
    grade: 10,
    prerequisites: ['expand-brackets'],
    templateId: 'factorise-trinomial',
    lessonId: 'factorise-trinomial',
    mappings: caps(10, 'Algebraic expressions'),
  },
  {
    id: 'exponents',
    topicId: 'algebra',
    name: 'Laws of exponents',
    outcome: 'Simplify and evaluate expressions with exponents, including zero and negative exponents.',
    signature: 'a^m \\times a^n = a^{m+n}',
    grade: 10,
    prerequisites: ['multiplication'],
    templateId: 'exponents',
    lessonId: 'exponents',
    mappings: caps(10, 'Exponents and surds'),
  },
  {
    id: 'linear-equations',
    topicId: 'equations',
    name: 'Solving linear equations',
    outcome: 'Solve linear equations, including brackets and unknowns on both sides.',
    signature: '2x + 4 = 10',
    grade: 8,
    prerequisites: ['integers'],
    templateId: 'linear-equations',
    lessonId: 'linear-equations',
    mappings: caps(8, 'Algebraic equations'),
  },
  {
    id: 'quadratic-equations',
    topicId: 'equations',
    name: 'Quadratic equations by factorising',
    outcome: 'Solve quadratic equations by factorising and using the zero-product rule.',
    signature: 'x^2 + 5x + 6 = 0',
    grade: 10,
    prerequisites: ['factorise-trinomial', 'linear-equations'],
    templateId: 'quadratic-equations',
    lessonId: 'quadratic-equations',
    mappings: caps(10, 'Equations and inequalities'),
  },
  {
    id: 'gradient',
    topicId: 'functions',
    name: 'Gradient between two points',
    outcome: 'Calculate the gradient of a line through two points and interpret it.',
    signature: 'm = \\dfrac{y_2 - y_1}{x_2 - x_1}',
    grade: 9,
    prerequisites: ['integers', 'fraction-simplify'],
    templateId: 'gradient',
    lessonId: 'gradient',
    mappings: caps(9, 'Graphs'),
  },
  {
    id: 'straight-line',
    topicId: 'functions',
    name: 'Straight-line graphs',
    outcome: 'Connect y = mx + c to its graph: gradient, intercepts and equation.',
    signature: 'y = mx + c',
    grade: 10,
    prerequisites: ['gradient', 'linear-equations'],
    templateId: 'straight-line',
    lessonId: 'straight-line',
    mappings: caps(10, 'Functions'),
  },
  {
    id: 'quadratic-formula',
    topicId: 'equations',
    name: 'The quadratic formula',
    outcome: 'Solve any quadratic equation with the formula and use the discriminant to describe its roots.',
    signature: 'x = \\dfrac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}',
    grade: 11,
    prerequisites: ['quadratic-equations'],
    templateId: 'quadratic-formula',
    lessonId: 'quadratic-formula',
    mappings: caps(11, 'Equations and inequalities'),
  },
  {
    id: 'sequences',
    topicId: 'patterns',
    name: 'Arithmetic and geometric sequences',
    outcome: 'Find terms, general terms and sums of arithmetic and geometric sequences.',
    signature: 'T_n = a + (n-1)d',
    grade: 10,
    prerequisites: ['linear-equations'],
    templateId: 'sequences',
    lessonId: 'sequences',
    mappings: caps(12, 'Number patterns, sequences and series'),
  },
  {
    id: 'trig-ratios',
    topicId: 'trigonometry',
    name: 'Trigonometric ratios',
    outcome: 'Use sin, cos and tan to find sides and angles in right-angled triangles.',
    signature: '\\sin\\theta = \\dfrac{\\text{opp}}{\\text{hyp}}',
    grade: 10,
    prerequisites: ['linear-equations'],
    templateId: 'trig-ratios',
    lessonId: 'trig-ratios',
    mappings: caps(10, 'Trigonometry'),
  },
  {
    id: 'finance',
    topicId: 'finance',
    name: 'Simple and compound interest',
    outcome: 'Calculate simple and compound interest, growth and depreciation.',
    signature: 'A = P(1 + i)^n',
    grade: 10,
    prerequisites: ['exponents'],
    templateId: 'finance',
    lessonId: 'finance',
    mappings: caps(10, 'Finance, growth and decay'),
  },
  {
    id: 'derivatives',
    topicId: 'calculus',
    name: 'Derivatives',
    outcome: 'Differentiate polynomials with the power rule and find the gradient of a tangent.',
    signature: "f'(x) = nx^{n-1}",
    grade: 12,
    prerequisites: ['straight-line', 'exponents'],
    templateId: 'derivatives',
    lessonId: 'derivatives',
    mappings: caps(12, 'Differential calculus'),
  },
  // ---------- Natural Sciences & Technology (Grades 4–6) ----------
  {
    id: 'living-nonliving',
    topicId: 'nst-life',
    name: 'Living and non-living things',
    outcome: 'Decide whether something is living, non-living or was once living, using the seven life processes.',
    signature: '\\text{MRS GREN}',
    grade: 4,
    prerequisites: [],
    templateId: 'living-nonliving',
    lessonId: 'living-nonliving',
    mappings: caps(4, 'Life and living: living and non-living things'),
  },
  {
    id: 'plant-needs',
    topicId: 'nst-life',
    name: 'What plants need to grow',
    outcome: 'Name the parts of a plant and what each does, and test what a plant needs to grow.',
    signature: '\\text{light} + \\text{water} + \\text{air} \\rightarrow \\text{growth}',
    grade: 4,
    prerequisites: [],
    templateId: 'plant-needs',
    lessonId: 'plant-needs',
    mappings: caps(4, 'Life and living: what plants need to grow'),
  },
  {
    id: 'life-cycles',
    topicId: 'nst-life',
    name: 'Life cycles',
    outcome: 'Order the stages in the life cycles of animals and plants, including metamorphosis.',
    signature: '\\text{egg} \\rightarrow \\text{larva} \\rightarrow \\text{pupa} \\rightarrow \\text{adult}',
    grade: 5,
    prerequisites: ['living-nonliving'],
    templateId: 'life-cycles',
    lessonId: 'life-cycles',
    mappings: caps(5, 'Life and living: life cycles'),
  },
  {
    id: 'matter-properties',
    topicId: 'nst-matter',
    name: 'Solids, liquids and gases',
    outcome: 'Describe solids, liquids and gases by their properties, and explain melting and freezing.',
    signature: '\\text{solid} \\cdot \\text{liquid} \\cdot \\text{gas}',
    grade: 4,
    prerequisites: [],
    templateId: 'matter-properties',
    lessonId: 'matter-properties',
    mappings: caps(4, 'Matter and materials: solids, liquids and gases'),
  },
  {
    id: 'metals-nonmetals',
    topicId: 'nst-matter',
    name: 'Metals and non-metals',
    outcome: 'Test materials to sort them into metals and non-metals, and match materials to their uses.',
    signature: '\\text{shiny} \\cdot \\text{conducts} \\cdot \\text{magnetic?}',
    grade: 5,
    prerequisites: ['matter-properties'],
    templateId: 'metals-nonmetals',
    lessonId: 'metals-nonmetals',
    mappings: caps(5, 'Matter and materials: metals and non-metals'),
  },
  {
    id: 'mixtures',
    topicId: 'nst-matter',
    name: 'Mixtures and solutions',
    outcome: 'Tell soluble from insoluble, and choose a method to separate a mixture.',
    signature: '\\text{solute} + \\text{solvent} = \\text{solution}',
    grade: 6,
    prerequisites: ['matter-properties'],
    templateId: 'mixtures',
    lessonId: 'mixtures',
    mappings: caps(6, 'Matter and materials: mixtures and solutions'),
  },
  {
    id: 'sound',
    topicId: 'nst-energy',
    name: 'Sound and vibrations',
    outcome: 'Explain how sounds are made, and what changes pitch and loudness.',
    signature: '\\text{vibration} \\rightarrow \\text{sound}',
    grade: 4,
    prerequisites: [],
    templateId: 'sound',
    lessonId: 'sound',
    mappings: caps(4, 'Energy and change: energy and sound'),
  },
  {
    id: 'simple-circuits',
    topicId: 'nst-energy',
    name: 'Simple electric circuits',
    outcome: 'Build complete circuits, choose conductors and insulators, and use switches.',
    signature: '\\text{cell} \\rightarrow \\text{bulb} \\rightarrow \\text{cell}',
    grade: 6,
    prerequisites: [],
    templateId: 'simple-circuits',
    lessonId: 'simple-circuits',
    mappings: caps(6, 'Energy and change: electric circuits'),
  },
  {
    id: 'earth-moon',
    topicId: 'nst-earth',
    name: 'The Sun, Earth and Moon',
    outcome: 'Explain day and night, years and the phases of the Moon.',
    signature: '\\text{1 day} = \\text{1 turn of Earth}',
    grade: 4,
    prerequisites: [],
    templateId: 'earth-moon',
    lessonId: 'earth-moon',
    mappings: caps(6, 'Planet Earth and beyond: the Sun, Earth and Moon'),
  },
  {
    id: 'solar-system',
    topicId: 'nst-earth',
    name: 'The solar system',
    outcome: 'Know the order and features of the planets, and compare their sizes and distances.',
    signature: '\\text{Mercury} \\rightarrow \\dots \\rightarrow \\text{Neptune}',
    grade: 6,
    prerequisites: ['earth-moon'],
    templateId: 'solar-system',
    lessonId: 'solar-system',
    mappings: caps(6, 'Planet Earth and beyond: the solar system'),
  },
  {
    id: 'structures',
    topicId: 'nst-tech',
    name: 'Strong structures',
    outcome: 'Recognise frame, shell and solid structures, and make structures stronger with shapes and folds.',
    signature: '\\triangle \\text{ is rigid}',
    grade: 5,
    prerequisites: [],
    templateId: 'structures',
    lessonId: 'structures',
    mappings: caps(5, 'Technology: structures'),
  },
  // ---------- Physical Sciences ----------
  // Grades 7–9 come from CAPS Natural Sciences (Matter and Materials, Energy and Change); 10–12 from Physical Sciences.
  {
    id: 'states-of-matter',
    topicId: 'matter',
    name: 'States of matter and the particle model',
    outcome: 'Predict whether a substance is solid, liquid or gas, and explain it with particles.',
    signature: '\\text{solid} \\rightleftharpoons \\text{liquid} \\rightleftharpoons \\text{gas}',
    grade: 7,
    prerequisites: [],
    templateId: 'states-of-matter',
    lessonId: 'states-of-matter',
    mappings: caps(8, 'Matter and materials: particle model of matter'),
  },
  {
    id: 'density',
    topicId: 'matter',
    name: 'Density: will it float?',
    outcome: 'Calculate density, mass or volume, and predict floating and sinking.',
    signature: '\\rho = \\dfrac{m}{V}',
    grade: 8,
    prerequisites: ['states-of-matter'],
    templateId: 'density',
    lessonId: 'density',
    mappings: caps(8, 'Matter and materials: particle model of matter'),
  },
  {
    id: 'heat-transfer',
    topicId: 'energy',
    name: 'Heat transfer',
    outcome: 'Explain conduction, convection and radiation, and choose conductors and insulators.',
    signature: '\\text{hot} \\xrightarrow{\\;Q\\;} \\text{cold}',
    grade: 7,
    prerequisites: [],
    templateId: 'heat-transfer',
    lessonId: 'heat-transfer',
    mappings: caps(7, 'Energy and change: heat transfer'),
  },
  {
    id: 'mechanical-energy',
    topicId: 'energy',
    name: 'Potential and kinetic energy',
    outcome: 'Calculate gravitational potential and kinetic energy, and use conservation of energy.',
    signature: 'mgh = \\tfrac{1}{2}mv^2',
    grade: 10,
    prerequisites: ['speed-distance-time'],
    templateId: 'mechanical-energy',
    lessonId: 'mechanical-energy',
    mappings: caps(10, 'Mechanics: energy'),
  },
  {
    id: 'circuits',
    topicId: 'electricity',
    name: 'Series and parallel circuits',
    outcome: 'Compare series and parallel circuits, and calculate resistance and current in parallel.',
    signature: '\\tfrac{1}{R_p} = \\tfrac{1}{R_1} + \\tfrac{1}{R_2}',
    grade: 9,
    prerequisites: [],
    templateId: 'circuits',
    lessonId: 'circuits',
    mappings: caps(9, 'Energy and change: series and parallel circuits'),
  },
  {
    id: 'speed-distance-time',
    topicId: 'mechanics',
    name: 'Speed, distance and time',
    outcome: 'Calculate speed, distance or time, converting units where needed.',
    signature: 'v = \\dfrac{\\Delta x}{\\Delta t}',
    grade: 9,
    prerequisites: [],
    templateId: 'speed-distance-time',
    lessonId: 'speed-distance-time',
    mappings: caps(10, 'Mechanics: motion in one dimension'),
  },
  {
    id: 'newtons-second-law',
    topicId: 'mechanics',
    name: "Newton's second law",
    outcome: 'Use F = ma with net force, including friction.',
    signature: 'F_{\\text{net}} = ma',
    grade: 11,
    prerequisites: ['speed-distance-time'],
    templateId: 'newtons-second-law',
    lessonId: 'newtons-second-law',
    mappings: caps(11, "Mechanics: Newton's laws"),
  },
  {
    id: 'projectile-motion',
    topicId: 'mechanics',
    name: 'Projectile motion',
    outcome: 'Predict flight time, maximum height and range without air resistance.',
    signature: 'y = v_0 t - \\tfrac{1}{2} g t^2',
    grade: 12,
    prerequisites: ['newtons-second-law'],
    templateId: 'projectile-motion',
    lessonId: 'projectile-motion',
    mappings: caps(12, 'Mechanics: vertical projectile motion'),
  },
  {
    id: 'ohms-law',
    topicId: 'electricity',
    name: "Ohm's law and series circuits",
    outcome: 'Use V = IR and add resistances in series.',
    signature: 'V = IR',
    grade: 10,
    prerequisites: [],
    templateId: 'ohms-law',
    lessonId: 'ohms-law',
    mappings: caps(10, 'Electricity and magnetism: electric circuits'),
  },
  {
    id: 'atomic-structure',
    topicId: 'matter',
    name: 'Atomic structure',
    outcome: 'Work out protons, neutrons and electrons for atoms and ions.',
    signature: '{}^{23}_{11}\\text{Na}',
    grade: 10,
    prerequisites: [],
    templateId: 'atomic-structure',
    lessonId: 'atomic-structure',
    mappings: caps(10, 'Matter and materials: the atom'),
  },
  {
    id: 'acids-bases',
    topicId: 'chemical-change',
    name: 'Acids, bases and pH',
    outcome: 'Use the pH scale and indicators, and predict the products of neutralisation.',
    signature: '\\text{acid} + \\text{base} \\rightarrow \\text{salt} + \\text{water}',
    grade: 9,
    prerequisites: [],
    templateId: 'acids-bases',
    lessonId: 'acids-bases',
    mappings: caps(9, 'Matter and materials: acids, bases and pH'),
  },
  {
    id: 'moles',
    topicId: 'chemical-change',
    name: 'The mole',
    outcome: 'Convert between mass and amount of substance using molar mass.',
    signature: 'n = \\dfrac{m}{M}',
    grade: 10,
    prerequisites: ['atomic-structure'],
    templateId: 'moles',
    lessonId: 'moles',
    mappings: caps(10, 'Chemical change: quantitative aspects'),
  },
  {
    id: 'balancing-equations',
    topicId: 'chemical-change',
    name: 'Balancing chemical equations',
    outcome: 'Balance equations so that atoms are conserved.',
    signature: '2\\text{H}_2 + \\text{O}_2 \\rightarrow 2\\text{H}_2\\text{O}',
    grade: 9,
    prerequisites: ['atomic-structure'],
    templateId: 'balancing-equations',
    lessonId: 'balancing-equations',
    mappings: caps(10, 'Chemical change: representing reactions'),
  },
  // ---------- Life Sciences ----------
  // Grades 7–9 come from CAPS Natural Sciences (Life and Living); 10–12 from Life Sciences.
  {
    id: 'classification',
    topicId: 'biodiversity',
    name: 'Classifying animals',
    outcome: 'Sort animals into vertebrate classes and arthropod groups from their features.',
    signature: '\\text{backbone?} \\rightarrow \\text{vertebrate}',
    grade: 7,
    prerequisites: [],
    templateId: 'classification',
    lessonId: 'classification',
    mappings: caps(7, 'Life and living: biodiversity'),
  },
  {
    id: 'food-chains',
    topicId: 'ecology',
    name: 'Food chains and energy flow',
    outcome: 'Read food chains, follow energy through trophic levels, and predict knock-on effects.',
    signature: '\\text{grass} \\rightarrow \\text{impala} \\rightarrow \\text{lion}',
    grade: 8,
    prerequisites: [],
    templateId: 'food-chains',
    lessonId: 'food-chains',
    mappings: caps(8, 'Life and living: interactions and interdependence'),
  },
  {
    id: 'photosynthesis',
    topicId: 'plants',
    name: 'Photosynthesis and respiration',
    outcome: 'Explain what plants take in and give out, what limits photosynthesis, and the net gas balance.',
    signature: '\\text{CO}_2 + \\text{H}_2\\text{O} \\xrightarrow{\\text{light}} \\text{glucose} + \\text{O}_2',
    grade: 8,
    prerequisites: [],
    templateId: 'photosynthesis',
    lessonId: 'photosynthesis',
    mappings: caps(8, 'Life and living: photosynthesis and respiration'),
  },
  {
    id: 'body-systems',
    topicId: 'human-body',
    name: 'Circulation, breathing and digestion',
    outcome: 'Trace blood, air and food through the body, and say what each organ does.',
    signature: '\\text{RA} \\rightarrow \\text{RV} \\rightarrow \\text{lungs} \\rightarrow \\text{LA} \\rightarrow \\text{LV}',
    grade: 9,
    prerequisites: [],
    templateId: 'body-systems',
    lessonId: 'body-systems',
    mappings: caps(9, 'Life and living: systems in the human body'),
  },
  {
    id: 'cell-structure',
    topicId: 'cells',
    name: 'Cell structure and function',
    outcome: 'Identify organelles, their functions, and how plant and animal cells differ.',
    signature: '\\text{structure} \\leftrightarrow \\text{function}',
    grade: 10,
    prerequisites: [],
    templateId: 'cell-structure',
    lessonId: 'cell-structure',
    mappings: caps(10, 'Life at the molecular, cellular and tissue level'),
  },
  {
    id: 'cell-division',
    topicId: 'cells',
    name: 'Cell division: mitosis and meiosis',
    outcome: 'Order the phases of mitosis and track chromosome numbers through mitosis and meiosis.',
    signature: '2n \\xrightarrow{\\text{mitosis}} 2n,\\ 2n',
    grade: 10,
    prerequisites: ['cell-structure'],
    templateId: 'cell-division',
    lessonId: 'cell-division',
    mappings: caps(10, 'Life at the molecular, cellular and tissue level: mitosis'),
  },
  {
    id: 'monohybrid-crosses',
    topicId: 'genetics',
    name: 'Monohybrid crosses',
    outcome: 'Use Punnett squares to predict genotype and phenotype probabilities.',
    signature: '\\text{Aa} \\times \\text{Aa} \\rightarrow 1 : 2 : 1',
    grade: 12,
    prerequisites: ['fraction-simplify'],
    templateId: 'monohybrid-crosses',
    lessonId: 'monohybrid-crosses',
    mappings: caps(12, 'Genetics and inheritance'),
  },
  ...FOUNDATION_SKILLS,
];

// ---------- Lookups ----------

const skillById = new Map(SKILLS.map((s) => [s.id, s]));
const topicById = new Map(TOPICS.map((t) => [t.id, t]));
const subjectById = new Map(SUBJECTS.map((s) => [s.id, s]));

export const getSkill = (id: string): Skill | undefined => skillById.get(id);
export const getTopic = (id: string): Topic | undefined => topicById.get(id);
export const getSubject = (id: string): Subject | undefined => subjectById.get(id as SubjectId);

export function subjectOfSkill(skill: Skill): SubjectId {
  return (topicById.get(skill.topicId) as Topic).subjectId;
}

export const skillsInTopic = (topicId: string): Skill[] => SKILLS.filter((s) => s.topicId === topicId);
export const topicsInSubject = (subjectId: SubjectId): Topic[] => TOPICS.filter((t) => t.subjectId === subjectId);
export const skillsInSubject = (subjectId: SubjectId): Skill[] => SKILLS.filter((s) => subjectOfSkill(s) === subjectId);

export const isFoundationSkill = (s: Skill): boolean => s.phase === 'foundation';

/**
 * Skills that are relevant for a learner: in their subjects, taught up to one grade ahead,
 * and no more than four grades behind (foundations still matter).
 * Grades 1–3 get the Foundation Phase skills of their own grade and the grade before (to catch up);
 * Grade 4 and up never get Foundation Phase skills here: they are played in the Junior experience.
 */
export function skillsForLearner(subjects: SubjectId[], grade: number): Skill[] {
  if (grade <= 3) return SKILLS.filter((s) => isFoundationSkill(s) && subjects.includes(subjectOfSkill(s)) && s.grade <= grade && s.grade >= grade - 1);
  return SKILLS.filter((s) => !isFoundationSkill(s) && subjects.includes(subjectOfSkill(s)) && s.grade <= grade + 1 && s.grade >= grade - 4);
}

export const FOUNDATION_SUBJECTS: SubjectId[] = ['mathematics', 'home-language', 'english-fal', 'life-skills'];

/** Which subjects make sense at a grade. Natural Sciences & Technology stops after Grade 7. */
export function subjectsForGrade(grade: number): SubjectId[] {
  if (grade <= 3) return FOUNDATION_SUBJECTS;
  return grade <= 7 ? ['mathematics', 'natural-sciences', 'physical-sciences', 'life-sciences'] : ['mathematics', 'physical-sciences', 'life-sciences'];
}

/** What to tick by default for a new learner at a grade. */
export function defaultSubjects(grade: number): SubjectId[] {
  if (grade <= 3) return FOUNDATION_SUBJECTS;
  return grade <= 6 ? ['mathematics', 'natural-sciences'] : grade <= 9 ? ['mathematics', 'physical-sciences', 'life-sciences'] : ['mathematics', 'physical-sciences'];
}
